import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { z } from 'zod'
import type { DynamicFormSpec } from '@internal/ui-kit/components/app/DynamicFormRenderer'
import { httpClient } from '@internal/ui-kit/lib/httpClient'
import type { cartProductSchema, cartSchema } from './schema'

export type Cart = z.infer<typeof cartSchema>
export type CartProduct = z.infer<typeof cartProductSchema>

/**
 * `getSelectedUserOrder` (`GET admin/orders/by-user/{user_id}/current`) — a
 * bespoke, non-listable endpoint scoped to one user's in-progress cart, not
 * a real `dataProvider` resource (same reasoning `useOrderEvents.ts`
 * documents for its own single-order-scoped GET).
 */
export function useCart(userId: string | undefined) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ['user-cart', userId],
    queryFn: () => httpClient<Cart>(`/admin/orders/by-user/${userId}/current`),
    enabled: Boolean(userId),
  })

  return { cart: data, isPending, isFetching, error, refetch }
}

export type DeliveryAddress = {
  delivery_address_id: string
  owner_id: string
  region: string
  region_code?: string | null
  city: string
  city_code?: string | null
  barangay: string
  barangay_code?: string | null
  street: string
  floor_or_unit_no?: string | null
  zip_code: string
}

/**
 * `GET delivery-addresses/by_user/{user_id}` (HATCH-519 — ticket feedback:
 * "change address should show list of existing addresses with ability to
 * choose... and then there should be button add new there") — every address
 * already on file for this user, confirmed via `oaq.py`. Bespoke, not the
 * registered `dataProvider` (no `delivery-addresses` resource is
 * registered) — same reasoning every other call in this file already
 * documents for a single-purpose, non-listable-by-the-usual-convention GET.
 */
export function useDeliveryAddressesByUser(userId: string | undefined) {
  const { data, isPending } = useQuery({
    queryKey: ['delivery-addresses-by-user', userId],
    queryFn: () => httpClient<DeliveryAddress[]>(`/delivery-addresses/by_user/${userId}`),
    enabled: Boolean(userId),
  })
  return { deliveryAddresses: data ?? [], isPending }
}

/**
 * `searchProducts` (`GET products?limit=50&query=`) — matches the source
 * export's own query exactly. Not the registered `products` `dataProvider`
 * resource: that resource's `getOne` is confirmed `v2/admin/products/{id}`
 * (HATCH-41 round 5), a different endpoint from the plain `products/{id}`
 * this flow needs (the one that returns each option's nested
 * `schedules`/`slots_number`/`needs_booking_details`/`form_id` — see
 * MIGRATION-NOTES.md).
 *
 * `placeholderData: keepPreviousData` — without it, every debounced query
 * change resets `data` to `undefined` for the length of the new fetch, so
 * `products` (below) goes to `[]` and the `Combobox`'s own client-side
 * (`cmdk`) filter against its now-empty `options` briefly shows "no results"
 * before the new page arrives (ticket: "can we fix search results
 * flashing?"). Keeping the previous page visible during the refetch removes
 * that empty flash.
 */
export function useCartProductSearch(query: string) {
  const { data, isPending } = useQuery({
    queryKey: ['cart-product-search', query],
    queryFn: () => httpClient<CartProduct[]>(`/products?limit=50&query=${encodeURIComponent(query)}`),
    placeholderData: keepPreviousData,
  })
  return { products: data ?? [], isPending }
}

/** `getSelectedProduct` (`GET products/{product_id}`) — see `useCartProductSearch` above for why this bypasses the registered `products` resource. */
export function useCartProduct(productId: string | undefined) {
  const { data, isPending } = useQuery({
    queryKey: ['cart-product', productId],
    queryFn: () => httpClient<CartProduct>(`/products/${productId}`),
    enabled: Boolean(productId),
  })
  return { product: data, isPending }
}

export type CartSlot = { start_time: string; end_time: string }

/**
 * `getSlots` (`GET products/{product_id}/schedules/{schedule_id}/slots`) —
 * the source export sends no `limit` param at all, which silently hit the
 * backend's own default page size (ticket feedback: "slots drop down
 * doesn't show all slots - only first 10"). An explicit high `limit`, the
 * same fix already applied to every other bespoke unpaginated list call in
 * this app (`useCartProductSearch`'s `limit=50`), asks for every slot
 * instead of just the backend's default page.
 */
export function useCartSlots(productId: string | undefined, scheduleId: string | undefined) {
  const { data, isPending } = useQuery({
    queryKey: ['cart-slots', productId, scheduleId],
    queryFn: () => httpClient<CartSlot[]>(`/products/${productId}/schedules/${scheduleId}/slots?limit=1000`),
    enabled: Boolean(productId && scheduleId),
  })
  return { slots: data ?? [], isPending }
}

type CartFormLink = { form_id: string; form: DynamicFormSpec }

/**
 * `GET form-links?product_id=&section_id=&schedule_id=&branch_id=` — the
 * same `form-links` resource `ProductFormLinksTable`/
 * `ScheduleLinkedFormsTable`/`LinkedFormsTable` manage (`apps/products`/
 * `apps/branches`), read here as a plain bespoke call instead of through
 * `dataProvider` (see `useCartProductSearch` above for why this whole flow
 * bypasses registered resources). Resolves whichever form is linked to this
 * specific product/section/schedule/branch combination — the higher-priority
 * tier of the booking-details form resolution (ticket: admin ignored this
 * tier entirely, always falling back to a static field set, so
 * `additional_booking_details` went out `null` for a product whose booking
 * form is only linked via `form-links`). `product.form_id` was a second
 * fallback tier below this one; dropped per review — it's deprecated.
 *
 * Params match `marketplace-frontend`'s own `useFetchLinkedFormsQuery({
 * sectionId, scheduleId, branchId, productId })` exactly (ticket feedback,
 * round 2 — the first pass guessed `option_id` instead of `section_id`, and
 * added a `form_type` filter the reference call doesn't send): the first
 * form in the response is the one to use — the backend does its own
 * prioritization when more than one link matches.
 */
export type CartDeliveryMethod = {
  delivery_method_id: string
  brand_id: string
  region_code?: string | null
  city_code?: string | null
  label?: string | null
  description?: string | null
  fee_amount?: number | null
}

/**
 * `getByBrand` (`GET delivery-methods/by-brand?brand_id=&region_code=&city_code=`,
 * HATCH-519) — every delivery method eligible for the given brands at one
 * region+city, keyed by `brand_id` (confirmed via `oaq.py`:
 * `Record<brand_id, DeliveryMethod[]>`). Bespoke, not the registered
 * `delivery-methods` resource `DeliveryMethodsTable.tsx` uses: that one
 * lists a single brand's methods unscoped by region for editing; this needs
 * exactly the methods valid for the address the admin just entered, across
 * every brand represented in the cart.
 */
export function useDeliveryMethodsByBrand(brandIds: string[], regionCode: string | undefined, cityCode: string | undefined) {
  const sortedBrandIds = [...brandIds].sort()
  const { data, isPending } = useQuery({
    queryKey: ['cart-delivery-methods-by-brand', sortedBrandIds, regionCode, cityCode],
    queryFn: () => {
      const params = new URLSearchParams()
      sortedBrandIds.forEach((brandId) => params.append('brand_id', brandId))
      params.set('region_code', regionCode as string)
      params.set('city_code', cityCode as string)
      return httpClient<Record<string, CartDeliveryMethod[]>>(`/delivery-methods/by-brand?${params.toString()}`)
    },
    enabled: sortedBrandIds.length > 0 && Boolean(regionCode) && Boolean(cityCode),
  })
  return { deliveryMethodsByBrand: data ?? {}, isPending }
}

export function useCartFormLinks(
  productId: string | undefined,
  sectionId: string | undefined,
  scheduleId: string | undefined,
  branchId: string | undefined,
) {
  const { data } = useQuery({
    queryKey: ['cart-form-links', productId, sectionId, scheduleId, branchId],
    queryFn: () => {
      const params = new URLSearchParams({ product_id: productId as string })
      if (sectionId) params.set('section_id', sectionId)
      if (scheduleId) params.set('schedule_id', scheduleId)
      if (branchId) params.set('branch_id', branchId)
      return httpClient<CartFormLink[]>(`/form-links?${params.toString()}`)
    },
    enabled: Boolean(productId),
  })
  return { formLinks: Array.isArray(data) ? data : [] }
}
